import { useEffect, MouseEvent, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { FormikErrors } from 'formik';
import { getAXNUIAttributeLabel } from "@iddataweb/idw-common-ui/utils";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { AttributeStatusTypes } from "@enums/attribute-status-types";
import { getLanguageContent } from '@redux/selectors/language-selectors';
import { getLocalizeStrings } from "@redux/selectors/verification-configuration-selectors";
import { setAttributeFormStateIsReadOnly } from '@redux/actions/verification-form-actions';
import { Logger } from "@utils/logger";
import { AXNUITextInput } from '@iddataweb/idw-common-ui/containers';
import styles from "./read-only-attribute-input.less";
import { AttributeTypes } from '@iddataweb/idw-common-ui/enums';
import { NATIONALITY_SELECT_OPTIONS } from '@constants/nationality';

type ReadOnlyAttributeInputProps = {
	attribute: AttributeFormState;
	fieldName: string;
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	onAddNewSubmitValue: (payload: any) => void;
	onSetPickerValue: (payload: any) => void;
};

export const ReadOnlyAttributeInput = (props: ReadOnlyAttributeInputProps) => {
	const dispatch = useDispatch();
	const {
		attribute,
		fieldName,
		setFieldValue,
		setFieldTouched,
		onAddNewSubmitValue,
		onSetPickerValue,
	} = props;

	const localizeStrings = useSelector(getLocalizeStrings);
    const content = useSelector(getLanguageContent);
	const previousAttributeData = attribute.attrList[0];

	useEffect(() => {
		if (attribute.status === AttributeStatusTypes.PICKER && !attribute.pickerData.id) {
			Logger.info('[ReadOnlyAttributeInput] set readonly picker value', previousAttributeData);
			setFieldValue(fieldName, previousAttributeData.attribute.masked);
			onSetPickerValue({
				pickerData: {
					id: previousAttributeData.attribute.id
				}
			});
		}
	}, [attribute.status, attribute.pickerData.id, previousAttributeData, fieldName]);

    const addNewValue = async (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

		dispatch(setAttributeFormStateIsReadOnly(attribute.id, false));

        const attrListAttribute = attribute.attrList.find(attrListAttr => attrListAttr.attribute?.id === attribute.pickerData?.id);
        if (!attribute.masked && attrListAttribute) {
            onAddNewSubmitValue(attrListAttribute.attribute.valuesMap);
            await setFieldValue(fieldName, attrListAttribute.attribute.masked);
			setFieldTouched(fieldName, true, true);
        } else {
            onAddNewSubmitValue({});
            await setFieldValue(fieldName, '');
			setFieldTouched(fieldName, false, true);
        }
    };
	const shouldDisplayAddNew = useMemo(() => {
		return AttributeStatusTypes.PICKER === attribute.status && attribute.isEditable;
	}, [attribute.status, attribute.isEditable]);

	const attributeLabel = useMemo(() => {
		return getAXNUIAttributeLabel(
			localizeStrings,
			content.labels.attributes[attribute.type],
			attribute.label
		)
	}, [
		localizeStrings,
		content.labels.attributes[attribute.type],
		attribute.label
	]);

	const attributeValue = useMemo(() => {
		if (previousAttributeData.attribute.attributeType === AttributeTypes.NATIONALITY) {
			return NATIONALITY_SELECT_OPTIONS.find(nso => nso.value === previousAttributeData.attribute.masked)?.label || previousAttributeData.attribute.masked;
		}
		return previousAttributeData.attribute.masked;
	}, [previousAttributeData.attribute]);

	return (
		<div className={styles.readOnlyAttributeInput}>
			<AXNUITextInput
				required={!attribute.optional}
				controlId={`attr${attribute.id}-readonly-input`}
				automationId={`attr${attribute.id}-readonly-input`}
				disabled={true}
				onBlur={() => { }}
				onChange={() => { }}
				title={undefined}
				value={attributeValue}
			/>
			{shouldDisplayAddNew && (
				<a
					id={`attr${previousAttributeData.assignedTo}-new-link`}
					href={""}
					onClick={addNewValue}
				>
					{content.form.readOnlyInput.addNew}{' '}
					{attributeLabel}
				</a>
			)}
		</div>
	);
};